import React, { useState, useEffect, useCallback, useRef } from "react";
import "./AccessibilityWidget.css";

const STORAGE_KEY = "a11y-settings";

const DEFAULT_SETTINGS = {
  fontSize: 100,
  highContrast: false,
  highlightLinks: false,
  dyslexiaFont: false,
  reducedMotion: false,
};

const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  const panelRef = useRef(null);
  const toggleRef = useRef(null);

  // Apply settings to the DOM
  const applySettings = useCallback((s) => {
    const root = document.documentElement;

    // Font size
    root.style.fontSize = s.fontSize + "%";

    // Toggle classes
    root.classList.toggle("a11y-high-contrast", s.highContrast);
    root.classList.toggle("a11y-highlight-links", s.highlightLinks);
    root.classList.toggle("a11y-dyslexia-font", s.dyslexiaFont);
    root.classList.toggle("a11y-reduced-motion", s.reducedMotion);
  }, []);

  // Persist and apply whenever settings change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    applySettings(settings);
  }, [settings, applySettings]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape" && open) {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        toggleRef.current &&
        !toggleRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const update = (key, value) =>
    setSettings((prev) => ({ ...prev, [key]: value }));

  const adjustFont = (delta) =>
    setSettings((prev) => ({
      ...prev,
      fontSize: Math.min(150, Math.max(75, prev.fontSize + delta)),
    }));

  const resetAll = () => setSettings({ ...DEFAULT_SETTINGS });

  return (
    <>
      {/* Floating toggle */}
      <button
        ref={toggleRef}
        className="a11y-widget__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label="Accessibility options"
        aria-expanded={open}
        aria-controls="a11y-panel"
      >
        {/* Wheelchair icon */}
        <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28" aria-hidden="true">
          <path d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm9 5H3v2h6.5v4l-2 7h2.2l1.8-6.3L13.3 20h2.2l-2-7V9H20V7Z"/>
        </svg>
      </button>

      {/* Settings panel */}
      {open && (
        <div
          ref={panelRef}
          id="a11y-panel"
          className="a11y-widget__panel"
          role="dialog"
          aria-label="Accessibility settings"
        >
          <div className="a11y-widget__panel-header">
            <h2 className="a11y-widget__panel-title">Accessibility</h2>
            <button
              className="a11y-widget__close"
              onClick={() => setOpen(false)}
              aria-label="Close accessibility panel"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Font Size */}
          <div className="a11y-widget__option">
            <span className="a11y-widget__label">Font Size</span>
            <div className="a11y-widget__font-controls">
              <button
                className="a11y-widget__font-btn"
                onClick={() => adjustFont(-10)}
                aria-label="Decrease font size"
              >
                A&minus;
              </button>
              <span className="a11y-widget__font-value">
                {settings.fontSize}%
              </span>
              <button
                className="a11y-widget__font-btn"
                onClick={() => adjustFont(10)}
                aria-label="Increase font size"
              >
                A+
              </button>
            </div>
          </div>

          {/* High Contrast */}
          <div className="a11y-widget__option">
            <span className="a11y-widget__label">High Contrast</span>
            <label className="a11y-widget__switch">
              <input
                type="checkbox"
                checked={settings.highContrast}
                onChange={(e) => update("highContrast", e.target.checked)}
                aria-label="Toggle high contrast"
              />
              <span className="a11y-widget__slider" />
            </label>
          </div>

          {/* Highlight Links */}
          <div className="a11y-widget__option">
            <span className="a11y-widget__label">Highlight Links</span>
            <label className="a11y-widget__switch">
              <input
                type="checkbox"
                checked={settings.highlightLinks}
                onChange={(e) => update("highlightLinks", e.target.checked)}
                aria-label="Toggle highlight links"
              />
              <span className="a11y-widget__slider" />
            </label>
          </div>

          {/* Dyslexia Font */}
          <div className="a11y-widget__option">
            <span className="a11y-widget__label">Dyslexia Font</span>
            <label className="a11y-widget__switch">
              <input
                type="checkbox"
                checked={settings.dyslexiaFont}
                onChange={(e) => update("dyslexiaFont", e.target.checked)}
                aria-label="Toggle dyslexia-friendly font"
              />
              <span className="a11y-widget__slider" />
            </label>
          </div>

          {/* Reduce Motion */}
          <div className="a11y-widget__option">
            <span className="a11y-widget__label">Reduce Motion</span>
            <label className="a11y-widget__switch">
              <input
                type="checkbox"
                checked={settings.reducedMotion}
                onChange={(e) => update("reducedMotion", e.target.checked)}
                aria-label="Toggle reduced motion"
              />
              <span className="a11y-widget__slider" />
            </label>
          </div>

          {/* Reset */}
          <button className="a11y-widget__reset" onClick={resetAll}>
            Reset All
          </button>
        </div>
      )}
    </>
  );
};

export default AccessibilityWidget;
